import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "purchase", label: "Buying a horse" },
  { value: "training", label: "Training program" },
  { value: "coaching", label: "Show coaching" },
  { value: "general", label: "General inquiry" },
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "general",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Please enter a valid email";
    if (!form.phone.trim()) e.phone = "Please enter your phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry sent — we'll be in touch soon.");
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "general",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F9F9F6] py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Left editorial copy */}
        <div className="md:col-span-5">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#965C3B]">
              Contact
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h2
              data-testid="contact-heading"
              className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] text-[#1A2B22]"
            >
              Let's find your
              <br />
              <em className="italic font-normal text-[#965C3B]">next partner.</em>
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-8 max-w-md text-[#1A2B22]/75 leading-relaxed">
              Whether you're searching for the right horse, considering a
              training move, or planning your show season — share a few
              details and we'll respond personally within one business day.
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-12 space-y-5 border-t hairline pt-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6A61]">
                  Email
                </p>
                <a
                  data-testid="contact-info-email"
                  href={`mailto:${SITE.email}`}
                  className="font-display text-2xl tracking-tighter text-[#1A2B22] hover:text-[#965C3B] transition-colors"
                >
                  {SITE.email}
                </a>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#5C6A61]">
                  Located
                </p>
                <p className="font-display text-2xl tracking-tighter text-[#1A2B22]">
                  {SITE.address}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right form */}
        <div className="md:col-span-7 lg:col-span-6 lg:col-start-7">
          <Reveal delay={150}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              noValidate
              className="bg-white border hairline p-8 md:p-12 space-y-8"
            >
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="py-12 text-center"
                >
                  <p className="font-display text-3xl tracking-tighter text-[#1A2B22]">
                    Thank you.
                  </p>
                  <p className="mt-3 text-[#5C6A61]">
                    Your inquiry is in — we'll respond shortly.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 inline-flex items-center gap-2 px-5 py-3 border border-[#1A2B22] text-[#1A2B22] text-xs uppercase tracking-[0.24em] hover:bg-[#1A2B22] hover:text-[#F9F9F6] transition-colors"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <FloatingField
                    label="Name"
                    testId="contact-input-name"
                    value={form.name}
                    onChange={handleChange("name")}
                    error={errors.name}
                  />
                  <div className="grid md:grid-cols-2 gap-8">
                    <FloatingField
                      label="Email"
                      type="email"
                      testId="contact-input-email"
                      value={form.email}
                      onChange={handleChange("email")}
                      error={errors.email}
                    />
                    <FloatingField
                      label="Phone"
                      type="tel"
                      testId="contact-input-phone"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      error={errors.phone}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#5C6A61] mb-3">
                      I'm interested in
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {INTERESTS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`contact-interest-${opt.value}`}
                          onClick={() =>
                            setForm((f) => ({ ...f, interest: opt.value }))
                          }
                          className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                            form.interest === opt.value
                              ? "bg-[#1A2B22] text-[#F9F9F6] border-[#1A2B22]"
                              : "bg-transparent text-[#1A2B22] border-[#1A2B22]/25 hover:border-[#1A2B22]"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#5C6A61] mb-3">
                      Message (optional)
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange("message")}
                      placeholder="Tell us a little about your goals, your horse, or what you're looking for…"
                      className="w-full bg-transparent border-b hairline border-[#1A2B22]/25 focus:border-[#1A2B22] outline-none py-3 text-[#1A2B22] placeholder:text-[#5C6A61]/60 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#1A2B22] text-[#F9F9F6] text-xs uppercase tracking-[0.24em] hover:bg-[#965C3B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send Inquiry"}
                    <span aria-hidden>→</span>
                  </button>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FloatingField = ({ label, type = "text", testId, value, onChange, error }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#5C6A61] mb-3">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      className={`w-full bg-transparent border-b py-3 text-[#1A2B22] outline-none transition-colors ${
        error
          ? "border-red-500"
          : "border-[#1A2B22]/25 focus:border-[#1A2B22]"
      }`}
    />
    {error && (
      <p
        data-testid={`${testId}-error`}
        className="mt-2 text-xs text-red-600"
      >
        {error}
      </p>
    )}
  </div>
);

export default Contact;
